import React from 'react';
import Link from 'next/link';
import { CoverContent } from '@bnnvara/cards/src/components/deprecated/GridCard/CoverContent/CoverContent';
import {
  GridCard,
  LAYOUT_MINIMAL,
  LAYOUT_RECTANGLE,
  LAYOUT_SQUARE,
} from '@bnnvara/cards/src/components/deprecated/GridCard/GridCard';
import { Author } from '@bnnvara/cards/src/components/shared/Author';
import { track } from '@bnnvara/analytics/src/track';
import { CommentCount } from '@bnnvara/cards/src/components/shared/CommentCount';
import { Title } from '@bnnvara/cards/src/components/shared/Title';
import { formatDate } from '@bnnvara/helpers';
import { CommentIcon } from '@bnnvara/icons/src/components/UserInterfaceIcons/CommentIcon';
import PropTypes from 'prop-types';
import ReviewRating from '@bnnvara/rating';
import { Small } from '@bnnvara/cards/src/components/shared/Small';
import resizeImageSource from '../../../helpers/resizeImageSource';
import { CardLabel } from '../CardContent/CardLabel';

import {
  CardContainer,
  CardImage,
  CardContent,
  TopContent,
  BottomContent,
  CardImageWrapper,
} from './styled';

export const allowedTypes = ['Article', 'Media', 'ContentPage'];

export const PublicationCard = ({
  __typename: publicationType,
  title,
  publishDate,
  persons,
  thumbnail,
  commentCount,
  commentsEnabled,
  ariaLabelName,
  layout,
  categories,
  brand,
  isThemePage,
  subTheme,
  labelOverRide,
  review,
  url,
  page,
  id,
}) => {
  const imageSrc = resizeImageSource(
    thumbnail?.url || '/static/fallbackImages/videofallback.jpg',
    500,
    1
  );

  const pubDate =
    publishDate && formatDate(new Date(publishDate), 'dd-MM-yyyy');
  if (layout) {
    return (
      <GridCard layout={layout} aria-label={ariaLabelName}>
        <CoverContent
          title={title}
          pubDate={pubDate}
          backgroundImage={imageSrc}
          commentCount={commentCount}
          commentsEnabled={commentsEnabled}
        />
      </GridCard>
    );
  }

  const authorName = persons && persons[0] ? persons[0].name : undefined;
  const brandName = brand?.name;
  const trackInfo = {
    category: page,
    action: 'click',
    label: `${publicationType.toLowerCase()}-${id}`,
  };

  return (
    <Link
      href={url}
      onClick={() => {
        track(trackInfo);
      }}
      passHref
    >
      <CardContainer variant={publicationType}>
        <CardImageWrapper>
          <CardImage src={imageSrc} alt={thumbnail?.altText || title} />
        </CardImageWrapper>
        <CardContent>
          <TopContent>
            {commentCount > 0 && commentsEnabled && (
              <CommentCount>
                <CommentIcon />
                {commentCount}
              </CommentCount>
            )}
          </TopContent>
          <BottomContent>
            <CardLabel
              brand={brand}
              categories={categories}
              subTheme={subTheme}
              variant={`${publicationType.toLowerCase()}Card`}
              overRide={labelOverRide}
            />
            <Title id={id}>{title}</Title>
            {review && (
              <ReviewRating
                review={{
                  rating: review.rating,
                  type: review.type,
                  author: persons?.length > 0 ? persons[0] : null,
                  itemReviewed: review.productName,
                  headline: title,
                  publicationDate: formatDate(
                    new Date(publishDate),
                    'yyyy-MM-dd HH:mm:ssxx'
                  ).replace(' ', 'T'),
                  reviewBody: null,
                  image: thumbnail?.url?.replace('/{format}', ''),
                }}
                variant="card"
              />
            )}
            <Small>
              {isThemePage && brandName && `${brandName}  • `} {pubDate}
              {authorName && <Author>{authorName}</Author>}
            </Small>
          </BottomContent>
        </CardContent>
      </CardContainer>
    </Link>
  );
};

PublicationCard.propTypes = {
  __typename: PropTypes.oneOf(allowedTypes),
  thumbnail: PropTypes.shape({
    url: PropTypes.string,
    altText: PropTypes.string,
  }),
  brand: PropTypes.shape({
    name: PropTypes.string,
  }),
  title: PropTypes.string.isRequired,
  name: PropTypes.string,
  publishDate: PropTypes.string.isRequired,
  persons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string,
    })
  ).isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
    })
  ).isRequired,
  subTheme: PropTypes.shape({
    id: PropTypes.string,
    slug: PropTypes.string,
    name: PropTypes.string,
    color: PropTypes.string,
    colorHover: PropTypes.string,
    colorActive: PropTypes.string,
    colorVisited: PropTypes.string,
  }),
  commentCount: PropTypes.number,
  commentsEnabled: PropTypes.bool,
  ariaLabelName: PropTypes.string,
  isThemePage: PropTypes.bool,
  layout: PropTypes.shape({
    mobile: PropTypes.oneOf([LAYOUT_MINIMAL, LAYOUT_SQUARE]),
    tablet: PropTypes.oneOf([LAYOUT_MINIMAL, LAYOUT_RECTANGLE, LAYOUT_SQUARE]),
    desktop: PropTypes.oneOf([LAYOUT_RECTANGLE, LAYOUT_SQUARE]),
  }),
  labelOverRide: PropTypes.string,
  review: PropTypes.shape({
    type: PropTypes.string,
    rating: PropTypes.number,
    productName: PropTypes.string,
  }),
  url: PropTypes.string,
  page: PropTypes.string,
  id: PropTypes.string,
};
